<template>
  <div class="year-list">
    <pro-list-frame
      v-if="isDesktopListLayout"
      ref="plTable"
      :title="$t('financialYear')"
      icon="mdi-calendar-range"
      :total="totalItems"
      :rows="items.length"
      :search.sync="search"
      :filterable="false"
      :columns="toggleableHeaders"
      :hidden="hiddenColumns"
      :selected-count="selected.length"
      :loading="loading"
      :cell-sel="cellSel"
      :create-label="$t('financialYear')"
      :fixed="!mobileShow"
      @refresh="getItems"
      @print="printStudio = true"
      @create="goTo('year')"
      @toggle-column="toggleColumn"
      @show-all-columns="hiddenColumns = []"
      @cell-down="onCellDown"
      @cell-over="onCellOver"
      @clear-cells="clearCellSel"
      @clear-selection="selected = []"
    >
      <template v-if="$store.state.permissions && $store.state.permissions.includes('logList')" #tools>
        <v-menu offset-y left>
          <template v-slot:activator="{ attrs, on }">
            <button type="button" class="pl-btn pl-btn--icon" v-bind="attrs" v-on="on">
              <v-icon size="16">mdi-dots-vertical</v-icon>
            </button>
          </template>
          <v-list dense>
            <log ref="logRef" :type-prop="type" :listProp="true" mode-prop="button" />
          </v-list>
        </v-menu>
      </template>
      <template #bulk>
        <button type="button" class="pl-bulk__btn pl-bulk__btn--danger" @click="deletDialog = true">
          <v-icon size="17">mdi-trash-can-outline</v-icon>
          {{ $t("remove") }}
        </button>
      </template>

      <v-data-table
        v-if="headers"
        v-model="selected"
        :height="mobileShow ? 323 : plTableH"
        fixed-header
        :headers="tableHeaders"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="false"
        :page.sync="page"
        multi-sort
        dense
        :show-select="showSelect"
        item-key="id"
        :footer-props="plFooterProps"
      >
        <template v-slot:no-data>
          <div class="pl-empty">
            <img src="@/assets/dashboard/no-data-cloud.png" alt="" />
            <p>{{ $t("no-data") }}</p>
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="pl-action-cell">
          <v-menu offset-y left>
            <template v-slot:activator="{ attrs, on }">
              <v-btn
                color="primary"
                class="pl-action-cell__btn"
                v-bind="attrs"
                icon
                x-small
                v-on="on"
              >
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item link @click="edit(item)">
                <v-list-item-icon><v-icon>mdi-pencil-outline</v-icon></v-list-item-icon>
                <v-list-item-title v-text="$t('edit')" />
              </v-list-item>
              <log
                :title-prop="$t('history')"
                icon-prop="mdi-history"
                :listProp="true"
                :type-prop="type"
                :refIdProp="item.id"
                mode-prop="button"
              />
              <v-list-item link @click="openDeleteDialog(item)">
                <v-list-item-icon><v-icon color="pink">mdi-trash-can-outline</v-icon></v-list-item-icon>
                <v-list-item-title class="pink--text" v-text="$t('remove')" />
              </v-list-item>
            </v-list>
          </v-menu>
          <span class="pl-action-cell__row pl-muted pl-num">{{
            faNum(parseInt(items.indexOf(item)) + parseInt(from))
          }}</span>
          </div>
        </template>
        <template v-slot:item.id="{ item }">
          <span class="pl-muted pl-num">{{ faNum(item.id) }}</span>
        </template>
        <template v-slot:item.name="{ item }">
          <a class="pl-key" @click="edit(item)">{{ item.name }}</a>
        </template>
        <template v-slot:item.start_date="{ item }">
          <span class="pl-num">{{ faNum(item.start_date) }}</span>
        </template>
        <template v-slot:item.end_date="{ item }">
          <span class="pl-num">{{ faNum(item.end_date) }}</span>
        </template>
        <template v-slot:item.user.name="{ item }">
          <v-tooltip v-if="item.user" top>
            <template v-slot:activator="{ on, attrs }">
              <span class="pl-user" v-bind="attrs" v-on="on">
                <span class="pl-user__avatar">{{ (item.user.firstname || item.user.name || "?").charAt(0) }}</span>
                {{ item.user.firstname }} {{ item.user.lastname }}
              </span>
            </template>
            <span>{{ faNum(item.time) }}</span>
          </v-tooltip>
        </template>
        <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
          {{ faNum(pageStart) }}–{{ faNum(pageStop) }} {{ $t("of") }} {{ faNum($numberFormat(itemsLength)) }}
        </template>
        <template v-slot:footer.prepend>
          <div class="pl-goto">
            <span>{{ $t("goToPage") }}</span>
            <input v-model.number="page" type="number" min="1" :max="pageCount" class="pl-goto__input pl-num" />
            <span class="pl-num">/ {{ faNum(pageCount) }}</span>
          </div>
        </template>
      </v-data-table>
    </pro-list-frame>

    <pro-list-mobile
      v-else
      :title="$t('financialYear')"
      :items="items"
      :total="totalItems"
      :loading="loading"
      :page="options.page || 1"
      :page-count="pageCount"
      :search.sync="search"
      :filterable="false"
      :printable="true"
      :create-label="$t('financialYear')"
      :card="mobileCard"
      :actions="mobileActions"
      @load-more="loadPage"
      @refresh="loadPage(1)"
      @print="printStudio = true"
      @create="goTo('year')"
    />

    <print-studio
      v-model="printStudio"
      report="accounting-years"
      :title="$t('financialYear')"
      :columns="printColumns"
      :source="printSource"
      @error="serverErrorHandeler"
    />
    <v-dialog
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      v-model="deletDialog"
      width="400"
    >
      <v-card>
        <v-col>
          <v-btn
            icon
            class="close-hover"
            @click="deletDialog = false"
          >
            <v-icon
              color="grey"
              large
            >
              mdi-close
            </v-icon>
          </v-btn>
        </v-col>
        <v-col>
          <v-img
            class="mx-auto"
            width="200"
            src="@/assets/stop.svg"
          />
        </v-col>
        <v-col
          class="mt-3 pr-7"
        >
          <p class="mx-auto font-weight-medium">
            {{ $t('are-you-sure-delete-this-case') }}
          </p>
        </v-col>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col cols="6">
              <v-btn
                v-if="selected.length > 0"
                color="primary"
                @click="removegroup"
              >
                {{ $t('remove') }}
              </v-btn>
              <v-btn
                v-if="selected.length == 0"
                color="primary"
                @click="remove"
              >
                {{ $t('remove') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="grey"
                outlined
                class="float-left"
                @click="deletDialog = false"
              >
                {{ $t('cancel') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <snakbar
      v-model="snakbar"
      :text="snakbarText"
      :active="snakbar"
      :color="snakbarColor"
    />
  </div>
</template>
<script>
  import General from '../../../../Mixins/general'
  import ProList from '@/Mixins/proList'
  import ProListFrame from '@/components/proList/ProListFrame.vue'
  import ProListMobile from '@/components/proList/ProListMobile.vue'
  import PrintStudio from '@/modules/print/PrintStudio.vue'
  export default {
    name: 'yearList',
    components: {
      Snakbar: () => import('../../components/general/snakbar/snakbar'),
      ProListFrame,
      ProListMobile,
      PrintStudio,
    },
    mixins: [General, ProList],
    props: ['input', 'refId', 'mobileShow'],
    data: () => ({
      pageCount: 1,
      page: 1,
      selected: [],
      printStudio: false,
      deletDialog: false,
      filter: false,
      selectedHeaders: [],
      productStatus: null,
      productStatusItems: null,
      model: null,
      from: 1,
      taskDialog: null,
      tag: null,
      temp: null,
      deleteItem: null,
      sellStatus: null,
      items: [],
      sortBy: [],
      dialog: null,
      timer: 0,
      totalItems: 0,
      snakbar: false,
      typeForm: null,
      taskId: null,
      snakbarText: null,
      loading: true,
      options: {
        itemsPerPage: 5,
      },
      headers: null,
      type: 'year',
      search: null,
    }),
    computed: {
      showSelect () {
        if(!this.isMobile) {
          return true
        } else {
          return false
        }
      },
      showHeaders () {
        var app = this
        return this.headers.filter(function (s) {
          return app.selectedHeaders.includes(s)
        })
      },
      proListKey () {
        return 'accounting-year-list-columns'
      },
      proListLocked () {
        return ['actions', 'row', 'name']
      },
      printColumns () {
        return this.plPrintColumns({
          id: { format: 'number', width: 0.6, defaultVisible: false },
          name: { width: 2 },
          'user.name': { width: 1.4 },
        })
      },
      printSource () {
        var app = this
        return {
          currentRows: function () { return app.items || [] },
          total: function () { return app.totalItems || 0 },
          fetchPage: function (page, perPage) {
            return app.$axios
              .get(app.$endpoint + 'year', { params: app.listParams(page, Object.assign({}, app.options, { page: page, itemsPerPage: perPage })) })
              .then(function (res) {
                var d = res.data
                return { rows: d.data || [], lastPage: d.last_page || 1, total: d.total || 0 }
              })
          },
        }
      },
    },
    watch: {
      refId (val, oldVal) {
        // this.items = []
        this.getItems()
      },
      model () {
        this.$emit('input', this.model)
      },
      selected () {
        this.$emit('input', this.selected.text)
        if (this.selected.length > 0) {
          this.model = this.selected[0].text
          this.temp = this.selected[0].text
        }
        this.dialog = false
      },
      search () {
        var app = this
        app.page = 1
        app.loading = true
        clearTimeout(this.timer)
        this.timer = setTimeout(function () {
          app.getItems()
        }, 1000)
      },
      options: {
        handler () {
          var app = this
          app.sortBy = []
          app.options.sortBy.map(function (value, key) {
            const index = app.headers.findIndex(h => h.value === value)
            app.sortBy.push(app.headers[index].table)
          })
          this.getItems()
        },
        deep: true,
      },
      sortBy () {
        const index = this.headers.findIndex(h => h.value === this.sortBy)
        if (index > -1) {
          this.sortBy = this.headers[index].table
        }
      },
    },
    activated () {
      this.getItems()
    },
    beforeMount () {
      this.headers = [
        {
          text: this.$t('#'),
          align: 'center',
          sortable: false,
          value: 'actions',
          width: 42,
          class: 'pl-action-col',
          cellClass: 'pl-action-col',
        },
        {
          text: this.$t('id'),
          sortable: true,
          value: 'id',
          table: 'financial_detail_coding.id',
        },
        {
          text: this.$t('name'),
          sortable: true,
          value: 'name',
          table: 'financial_detail_coding.name',
        },
        {
          text: this.$t('startDate'),
          sortable: true,
          value: 'start_date',
          table: 'year.start_date',
        },
        {
          text: this.$t('endDate'),
          sortable: true,
          value: 'end_date',
          table: 'year.end_date',
        },
        {
          text: this.$t('registerar'),
          sortable: false,
          value: 'user.name',
        },
      ]
      if (this.$route.params.id != null) {
        this.show()
        this.id = this.$route.params.id
        this.render = false
      }
      this.selectedHeaders = this.headers
    },
    mounted () {
      if (this.input) {
        this.model = this.input
      }
      this.getItems()
    },
    methods: {
      openDeleteDialog (item) {
        this.deleteItem = item
        this.deletDialog = true
      },
      openTaskDialog (item) {
        this.refId = item.rel_id
        this.taskId = item.id
        this.taskDialog = true
      },
      isEnabled (slot) {
        return this.enabled === slot
      },
      edit (item) {
        this.$router.push({ name: 'yearEdit', params: { id: item.id } })
      },
      // Query params shared by the table and the print module.
      listParams (page, options) {
        return {
          search: this.search,
          page: page,
          sortBy: this.sortBy,
          status: this.productStatus,
          options: options,
        }
      },
      // Mobile paging (no data-table there to drive `options`).
      loadPage (page) {
        this.options = Object.assign({}, this.options, {
          page: page,
          itemsPerPage: this.options.itemsPerPage > 5 ? this.options.itemsPerPage : 10,
          sortBy: this.options.sortBy || [],
          sortDesc: this.options.sortDesc || [],
        })
      },
      mobileCard (item) {
        return {
          title: item.name,
          badge: this.faNum(item.start_date) + ' – ' + this.faNum(item.end_date),
          rows: [
            { label: this.$t('startDate'), value: this.faNum(item.start_date), num: true },
            { label: this.$t('endDate'), value: this.faNum(item.end_date), num: true },
            { label: this.$t('registerar'), value: item.user ? [item.user.firstname, item.user.lastname].filter(Boolean).join(' ') : '' },
          ],
        }
      },
      mobileActions (item) {
        return [
          { icon: 'mdi-pencil-outline', label: this.$t('edit'), handler: this.edit },
          { icon: 'mdi-trash-can-outline', label: this.$t('remove'), danger: true, handler: this.openDeleteDialog },
        ]
      },
      getItems () {
        var app = this
        app.loading = true
        app.$axios.get(this.$endpoint + 'year', {
          params: app.listParams(app.options.page, app.options),
        })
          .then(res => {
            res = res.data
            app.items = res.data
            app.totalItems = res.total
            app.pageCount = res.last_page
            app.from = res.from
          })
          .catch(err => {
            this.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      remove () {
        var app = this
        if (!app.deleteItem) return false
        app.loading = true
        app.$axios.delete(this.$endpoint + 'year/' + this.deleteItem.id)
          .then(res => {
            res = res.data
            app.deletDialog = false
            app.deleteItem = null
            app.getItems()
          })
          .catch(err => {
            this.deletDialog = false
            this.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      removegroup () {
        var app = this
        app.loading = true
        app.$axios.delete(this.$endpoint + 'year/0', {
          params: { deleteItems: app.selected },
        })
          .then(res => {
            res = res.data
            app.deletDialog = false
            app.deleteItem = null
            app.selected = []
            app.getItems()
          })
          .catch(err => {
            this.deletDialog = false
            this.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
    },
  }
</script>

<style scoped lang="scss">
::v-deep .v-data-table table thead tr th,
::v-deep .v-data-table table tbody tr td {
  white-space: nowrap;
}
::v-deep .v-text-field--outlined.v-input--dense.v-text-field--outlined > .v-input__control > .v-input__slot {
  min-height: 32px !important;
}
@import "~@/sass/design-system/list/pro-list";
</style>
